<script>
import { format, parseISO } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import QrCodeDialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Um ciclo de QR Code vale dois minutos. Passado esse tempo o desenho na tela
// não conecta mais nada, e continuar perguntando o estado só gera tráfego.
const QR_TTL_MS = 2 * 60 * 1000;

// Um tique por segundo move o contador regressivo; o estado da conexão é relido
// a cada dez tiques. Um único intervalo cuida das duas coisas, o que deixa a
// limpeza no unmount com um caminho só.
const TICK_MS = 1000;
const POLL_INTERVAL_MS = 10 * 1000;

// Quanto tempo o botão fica travado quando o servidor pede espera e não diz por
// quanto tempo. Nunca há nova tentativa automática: o limite que produz essa
// resposta é do servidor inteiro, compartilhado por vários clientes, e insistir
// sozinho atrapalha a conexão de outra empresa.
const DEFAULT_RETRY_AFTER_SECONDS = 20;

// A caixa aponta para uma conexão que não existe mais quando o servidor repassa
// uma recusa de autorização ou um "não encontrado". Ler o QR Code não resolve
// esse caso — alguém precisa apontar a caixa para a conexão nova —, então ele
// tem texto próprio em vez do genérico de falha de comunicação. O que sobra
// (tempo esgotado, servidor fora) cai no genérico, que não afirma causa.
const GONE_PATTERN =
  /\b401\b|\b404\b|unauthorized|invalid token|token inv|not found|n[aã]o encontrad/i;

// Data em formato numérico para caber ao lado da pastilha sem quebrar a linha.
const MOMENT_PATTERN = 'dd/MM/yyyy HH:mm';

export default {
  components: {
    SettingsFieldSection,
    Banner,
    QrCodeDialog,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      status: null,
      isFetchingStatus: false,
      hasFetchedOnce: false,
      isUnreachable: false,
      isGone: false,
      isOutOfScope: false,
      isConnecting: false,
      isDialogOpen: false,
      qrcode: '',
      isQrExpired: false,
      elapsedMs: 0,
      remainingMs: QR_TTL_MS,
      tickTimer: null,
      retryTimer: null,
      retryAfter: 0,
      ownerBeforeConnect: null,
      numberChange: null,
    };
  },
  computed: {
    // Ordem proposital: falha de comunicação vence o último estado conhecido.
    // Mostrar "fora do ar" por causa de uma resposta que não chegou faria alguém
    // abrir o QR Code de uma conexão que está no ar — e o ciclo de conexão
    // derruba o WhatsApp que estava funcionando.
    viewState() {
      if (!this.hasFetchedOnce) return 'loading';
      if (this.isGone) return 'gone';
      if (this.isUnreachable) return 'unreachable';
      if (this.status?.state === 'unknown') return 'unreachable';
      if (this.status?.connected) return 'connected';
      if (this.status?.state === 'connecting') return 'connecting';
      return 'disconnected';
    },
    isConnected() {
      return this.viewState === 'connected';
    },
    // O botão existe nos estados em que a leitura chegou. Em falha de
    // comunicação ele dá lugar ao "Tentar de novo", que não abre QR Code.
    showConnectButton() {
      return ['connected', 'connecting', 'disconnected'].includes(
        this.viewState
      );
    },
    isConnectDisabled() {
      return this.isConnected || this.isConnecting || this.retryAfter > 0;
    },
    connectButtonLabel() {
      if (this.retryAfter > 0) {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.RETRY_IN', {
          seconds: this.retryAfter,
        });
      }
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECT');
    },
    stateLabel() {
      // Uma chave literal por ramo: chave montada em tempo de execução escapa
      // da checagem de tradução faltando e some da extração de textos.
      if (this.viewState === 'connected') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATE.CONNECTED');
      }
      if (this.viewState === 'connecting') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATE.CONNECTING');
      }
      if (this.viewState === 'disconnected') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATE.DISCONNECTED');
      }
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATE.UNKNOWN');
    },
    stateBadgeClass() {
      const classes = {
        connected: 'bg-n-teal-3 text-n-teal-11',
        connecting: 'bg-n-amber-3 text-n-amber-11',
        disconnected: 'bg-n-ruby-3 text-n-ruby-11',
      };
      return classes[this.viewState] || 'bg-n-slate-3 text-n-slate-11';
    },
    numberLabel() {
      if (!this.status?.owner) {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.NUMBER_UNKNOWN');
      }
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.NUMBER', {
        number: this.status.owner,
      });
    },
    deviceLabel() {
      if (!this.status?.profile_name) return '';
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.DEVICE', {
        name: this.status.profile_name,
      });
    },
    lastDropLabel() {
      const when = this.formatMoment(this.status?.last_disconnect_at);
      if (!when) return '';
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.LAST_DROP', { when });
    },
    offlineSinceLabel() {
      const when = this.formatMoment(this.status?.last_disconnect_at);
      if (!when) {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.OFFLINE_SINCE_UNKNOWN');
      }
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.OFFLINE_SINCE', { when });
    },
    currentNumberLabel() {
      if (!this.ownerBeforeConnect) {
        return this.$t(
          'INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.CURRENT_NUMBER_UNKNOWN'
        );
      }
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.CURRENT_NUMBER', {
        number: this.ownerBeforeConnect,
      });
    },
    numberChangedLabel() {
      if (!this.numberChange) return '';
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.NUMBER_CHANGED', {
        before: this.numberChange.before,
        after: this.numberChange.after,
      });
    },
    // O código chega ora como data URI pronto, ora como base64 cru. Aceitar os
    // dois formatos evita quadrado em branco quando o outro lado muda a forma.
    qrcodeSrc() {
      const raw = String(this.qrcode || '').trim();
      if (!raw) return '';
      if (raw.startsWith('data:')) return raw;
      return `data:image/png;base64,${raw}`;
    },
    countdownLabel() {
      const totalSeconds = Math.max(0, Math.ceil(this.remainingMs / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.COUNTDOWN', {
        time: `${minutes}:${seconds}`,
      });
    },
  },
  watch: {
    'inbox.id'() {
      this.resetState();
      this.fetchStatus();
    },
  },
  mounted() {
    this.fetchStatus();
  },
  beforeUnmount() {
    // Polling é padrão novo nesta tela, então a limpeza é explícita e coberta
    // por teste: um intervalo esquecido continuaria perguntando o estado da
    // conexão depois que a pessoa saiu da página.
    this.stopPolling();
    this.clearRetryTimer();
  },
  methods: {
    resetState() {
      this.stopPolling();
      this.clearRetryTimer();
      this.status = null;
      this.hasFetchedOnce = false;
      this.isUnreachable = false;
      this.isGone = false;
      this.isOutOfScope = false;
      this.qrcode = '';
      this.isQrExpired = false;
      this.numberChange = null;
      this.ownerBeforeConnect = null;
      this.closeDialog();
    },
    formatMoment(value) {
      if (!value) return '';
      try {
        const parsed = parseISO(String(value));
        if (Number.isNaN(parsed.getTime())) return '';
        return format(parsed, MOMENT_PATTERN);
      } catch (error) {
        return '';
      }
    },
    async fetchStatus() {
      this.isFetchingStatus = true;
      try {
        const { data } = await InboxesAPI.getUazapiConnectionStatus(
          this.inbox.id
        );
        this.status = data;
        this.isGone = false;
        this.isUnreachable = data?.state === 'unknown';
        return data;
      } catch (error) {
        this.applyFetchError(error);
        return null;
      } finally {
        this.isFetchingStatus = false;
        this.hasFetchedOnce = true;
      }
    },
    applyFetchError(error) {
      const response = error?.response;
      // A caixa não é deste canal: o servidor recusa e a seção some, em vez de
      // ficar na tela dizendo que não conseguiu ler algo que não existe.
      if (response?.status === 400) {
        this.isOutOfScope = true;
        return;
      }
      // O servidor diz a causa em `reason`. A regex sobre a mensagem fica só como
      // retaguarda para resposta antiga, de container que ainda não subiu.
      const reason = response?.data?.reason;
      const message = String(response?.data?.error ?? '');
      this.isGone = reason ? reason === 'gone' : GONE_PATTERN.test(message);
      this.isUnreachable = !this.isGone;
    },
    async handleRetry() {
      this.isUnreachable = false;
      this.isGone = false;
      await this.fetchStatus();
    },
    async startConnection() {
      if (this.isConnecting || this.retryAfter > 0) return;

      this.isConnecting = true;
      this.numberChange = null;
      this.isQrExpired = false;
      this.qrcode = '';
      // O número de antes fica guardado para ser comparado com o que ficar
      // conectado no fim. É a checagem mais barata contra ler o código com o
      // celular errado e mandar as conversas da empresa para outro aparelho.
      this.ownerBeforeConnect = this.status?.owner || null;

      try {
        const { data } = await InboxesAPI.connectUazapi(this.inbox.id);
        this.qrcode = data?.qrcode || '';
        this.openDialog();
        this.startPolling();
      } catch (error) {
        this.handleConnectError(error);
      } finally {
        this.isConnecting = false;
      }
    },
    handleConnectError(error) {
      const response = error?.response;
      const httpStatus = response?.status;

      if (httpStatus === 409) {
        useAlert(this.$t('INBOX_MGMT.UAZAPI_CONNECTION.ALREADY_CONNECTED'));
        this.fetchStatus();
        return;
      }

      if (httpStatus === 429 || httpStatus === 503) {
        this.startRetryCountdown(this.readRetryAfter(response));
        return;
      }

      useAlert(this.$t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECT_ERROR'));
    },
    readRetryAfter(response) {
      const candidates = [
        Number(response?.data?.retry_after),
        Number(response?.headers?.['retry-after']),
      ];
      const seconds = candidates.find(
        value => Number.isFinite(value) && value > 0
      );
      return seconds || DEFAULT_RETRY_AFTER_SECONDS;
    },
    startRetryCountdown(seconds) {
      this.clearRetryTimer();
      this.retryAfter = seconds;
      this.retryTimer = setInterval(() => {
        this.retryAfter -= 1;
        if (this.retryAfter <= 0) {
          this.retryAfter = 0;
          this.clearRetryTimer();
        }
      }, TICK_MS);
    },
    clearRetryTimer() {
      if (!this.retryTimer) return;
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    },
    startPolling() {
      this.stopPolling();
      this.elapsedMs = 0;
      this.remainingMs = QR_TTL_MS;
      this.tickTimer = setInterval(this.handleTick, TICK_MS);
    },
    stopPolling() {
      if (!this.tickTimer) return;
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    },
    handleTick() {
      // A releitura periódica só existe enquanto o QR Code está na tela. Fora
      // disso ela é tráfego contra um servidor compartilhado por vários
      // clientes, sem ninguém olhando o resultado.
      if (!this.isDialogOpen) {
        this.stopPolling();
        return;
      }

      this.elapsedMs += TICK_MS;
      this.remainingMs = Math.max(0, QR_TTL_MS - this.elapsedMs);

      if (this.remainingMs <= 0) {
        this.expireQrCode();
        return;
      }

      if (this.elapsedMs % POLL_INTERVAL_MS === 0) this.pollStatus();
    },
    expireQrCode() {
      this.stopPolling();
      this.isQrExpired = true;
      this.qrcode = '';
    },
    async pollStatus() {
      const data = await this.fetchStatus();
      // Falha de comunicação no meio do ciclo não fecha o diálogo nem inventa
      // desconexão: o código na tela continua valendo até o contador zerar.
      if (!data) return;
      if (data.qrcode) this.qrcode = data.qrcode;
      if (data.connected) await this.handleConnected();
    },
    async handleConnected() {
      this.stopPolling();
      this.closeDialog();
      useAlert(this.$t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECTED_ALERT'));

      const previousOwner = this.ownerBeforeConnect;
      this.ownerBeforeConnect = null;

      const data = await this.fetchStatus();
      const currentOwner = data?.owner || null;
      if (previousOwner && currentOwner && previousOwner !== currentOwner) {
        this.numberChange = { before: previousOwner, after: currentOwner };
      }
    },
    openDialog() {
      this.isDialogOpen = true;
      this.$refs.qrDialog?.open();
    },
    closeDialog() {
      this.stopPolling();
      this.isDialogOpen = false;
      this.$refs.qrDialog?.close();
    },
    handleDialogClose() {
      this.stopPolling();
      this.isDialogOpen = false;
    },
    handleGenerateNewCode() {
      this.isQrExpired = false;
      this.startConnection();
    },
  },
};
</script>

<template>
  <div>
    <SettingsFieldSection
      v-if="!isOutOfScope"
      :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.TITLE')"
      :help-text="$t('INBOX_MGMT.UAZAPI_CONNECTION.DESCRIPTION')"
    >
      <div class="flex flex-col gap-3">
        <!-- Enquanto a leitura não volta a tela não afirma nada. Um "fora do ar"
           por padrão mandaria alguém abrir o código numa conexão saudável. -->
        <div
          v-if="viewState === 'loading'"
          data-testid="uazapi-skeleton"
          class="flex flex-col gap-2"
        >
          <div class="w-32 h-5 rounded-lg bg-n-slate-3 animate-pulse" />
          <div class="w-64 h-4 rounded-lg bg-n-slate-3 animate-pulse" />
          <span class="text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.CHECKING') }}
          </span>
        </div>

        <template v-else>
          <div class="flex gap-2 items-center">
            <span
              data-testid="uazapi-badge"
              class="px-2 py-1 rounded-lg text-label-small"
              :class="stateBadgeClass"
            >
              {{ stateLabel }}
            </span>
            <span
              v-if="deviceLabel && viewState !== 'unreachable'"
              data-testid="uazapi-device"
              class="text-body-main text-n-slate-12"
            >
              {{ deviceLabel }}
            </span>
          </div>

          <p
            v-if="numberChangedLabel"
            data-testid="uazapi-number-changed"
            class="mb-0 text-body-main text-n-ruby-11"
          >
            {{ numberChangedLabel }}
          </p>

          <template v-if="viewState === 'connected'">
            <p
              data-testid="uazapi-number"
              class="mb-0 text-body-main text-n-slate-12"
            >
              {{ numberLabel }}
            </p>
            <p
              v-if="lastDropLabel"
              class="mb-0 text-label-small text-n-slate-11"
            >
              {{ lastDropLabel }}
            </p>
            <p class="mb-0 text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECTED_HINT') }}
            </p>
          </template>

          <template
            v-else-if="
              viewState === 'disconnected' || viewState === 'connecting'
            "
          >
            <Banner color="ruby">
              <div class="flex flex-col gap-1">
                <span data-testid="uazapi-reason">
                  {{
                    status && status.reason_label
                      ? status.reason_label
                      : $t('INBOX_MGMT.UAZAPI_CONNECTION.STATE.DISCONNECTED')
                  }}
                </span>
                <span>{{ offlineSinceLabel }}</span>
              </div>
            </Banner>
            <p
              data-testid="uazapi-number"
              class="mb-0 text-body-main text-n-slate-12"
            >
              {{ numberLabel }}
            </p>
          </template>

          <template v-else-if="viewState === 'gone'">
            <Banner color="amber">
              <div class="flex flex-col gap-1" data-testid="uazapi-gone">
                <span>{{ $t('INBOX_MGMT.UAZAPI_CONNECTION.GONE.TITLE') }}</span>
                <span>{{ $t('INBOX_MGMT.UAZAPI_CONNECTION.GONE.HINT') }}</span>
              </div>
            </Banner>
          </template>

          <template v-else>
            <Banner color="slate">
              <div class="flex flex-col gap-1" data-testid="uazapi-unreachable">
                <span>
                  {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.UNREACHABLE.TITLE') }}
                </span>
                <span>
                  {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.UNREACHABLE.HINT') }}
                </span>
              </div>
            </Banner>
          </template>

          <div class="flex gap-2 items-center">
            <NextButton
              v-if="showConnectButton"
              data-testid="uazapi-connect-button"
              :label="connectButtonLabel"
              :disabled="isConnectDisabled"
              :is-loading="isConnecting"
              @click="startConnection"
            />
            <NextButton
              v-else
              faded
              slate
              data-testid="uazapi-retry-button"
              :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.UNREACHABLE.RETRY')"
              :is-loading="isFetchingStatus"
              @click="handleRetry"
            />
          </div>
        </template>

        <!-- Fixo em qualquer estado: é a frase que evita o prejuízo que abriu o
           chamado, e ela precisa estar na tela mesmo quando tudo está no ar. -->
        <p
          data-testid="uazapi-never-delete"
          class="mb-0 text-label-small text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.NEVER_DELETE') }}
        </p>
      </div>

      <QrCodeDialog
        ref="qrDialog"
        width="md"
        :title="$t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.TITLE')"
        :show-confirm-button="isQrExpired"
        :confirm-button-label="
          $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.NEW_CODE')
        "
        :cancel-button-label="$t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.CLOSE')"
        @confirm="handleGenerateNewCode"
        @close="handleDialogClose"
      >
        <div class="flex flex-col gap-3">
          <p
            data-testid="uazapi-dialog-current-number"
            class="mb-0 text-body-main text-n-slate-12"
          >
            {{ currentNumberLabel }}
          </p>

          <ol
            class="flex flex-col gap-1 pl-4 list-decimal text-body-main text-n-slate-11"
          >
            <li>{{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.STEP_1') }}</li>
            <li>{{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.STEP_2') }}</li>
            <li>{{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.STEP_3') }}</li>
          </ol>

          <div
            v-if="isQrExpired"
            data-testid="uazapi-qr-expired"
            class="flex flex-col gap-1"
          >
            <span class="text-body-main text-n-ruby-11">
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.EXPIRED') }}
            </span>
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.EXPIRED_HINT') }}
            </span>
          </div>

          <div v-else class="flex flex-col gap-2 items-center">
            <img
              v-if="qrcodeSrc"
              data-testid="uazapi-qr"
              class="w-56 h-56 bg-white rounded-xl"
              :src="qrcodeSrc"
              :alt="$t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.QR_ALT')"
            />
            <!-- O ciclo pode começar antes de o código existir; o próximo ciclo de
               leitura preenche a imagem. -->
            <span
              v-else
              data-testid="uazapi-qr-generating"
              class="text-body-main text-n-slate-11"
            >
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DIALOG.GENERATING') }}
            </span>
            <span
              data-testid="uazapi-countdown"
              class="text-label-small text-n-slate-11"
            >
              {{ countdownLabel }}
            </span>
          </div>
        </div>
      </QrCodeDialog>
    </SettingsFieldSection>
  </div>
</template>
